import { createContext, useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import type { z } from "zod";

import { useInvalidateEmployeeProfile } from "@/hooks/use-invalidate-employee-profile";
import { extractApiErrorMessage } from "@/lib/compliance/invalidate-compliance-queries";
import { serverFieldErrors } from "@/lib/people-profile-sections";

/** Lets the profile page know a card has an open edit, so a tab change can ask first. */
export const ProfileEditingContext = createContext<{
  register: () => () => void;
} | null>(null);

/**
 * Whether a section's card is showing its edit form. When the form closes,
 * focus goes back to the Edit button that opened it.
 */
export function useProfileSectionEditing() {
  const [editing, setEditing] = useState(false);
  const editButtonRef = useRef<HTMLButtonElement>(null);
  const returnFocus = useRef(false);
  const openEdits = useContext(ProfileEditingContext);

  useEffect(() => {
    if (!editing) return;
    return openEdits?.register();
  }, [editing, openEdits]);

  useEffect(() => {
    if (editing) return;
    if (!returnFocus.current) return;
    returnFocus.current = false;
    editButtonRef.current?.focus();
  }, [editing]);

  return {
    editing,
    editButtonRef,
    startEditing: () => setEditing(true),
    stopEditing: () => {
      returnFocus.current = true;
      setEditing(false);
    },
  };
}

/**
 * Form state for one profile section's edit form. The form only exists while
 * it is open, so it starts from the saved profile and a background refetch
 * can't wipe what has been typed. `validate` puts each schema error on its
 * field, keyed by path ("allergies.0.allergen").
 */
export function useProfileSectionForm<T extends object>(
  initialValues: () => T,
  schema: z.ZodType<T>,
) {
  const [values, setValues] = useState<T>(initialValues);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const setField = <K extends keyof T>(key: K, value: T[K]) =>
    setValues((current) => ({ ...current, [key]: value }));

  const validate = (): boolean => {
    const result = schema.safeParse(values);
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.issues.forEach((issue) => {
        fieldErrors[issue.path.join(".")] ??= issue.message;
      });
      setErrors(fieldErrors);
      return false;
    }
    setErrors({});
    return true;
  };

  const clearErrors = () => setErrors({});

  return {
    values,
    setValues,
    setField,
    errors,
    setErrors,
    validate,
    clearErrors,
  };
}

/**
 * Mutation options for every section's save: refetch the profile, confirm,
 * close; or say why not. A server validation 400 names its fields, so those
 * reasons go on the fields as well as in the toast. "always" makes an offline save fail and say so,
 * rather than pause, which would hold the form open until the network returns.
 */
export function useProfileSectionSave(
  nurseryId: string,
  userId: string,
  sectionName: string,
  onDone: () => void,
  showFieldErrors: (errors: Record<string, string>) => void,
) {
  const invalidateProfile = useInvalidateEmployeeProfile(nurseryId, userId);

  return {
    networkMode: "always" as const,
    onSuccess: async () => {
      await invalidateProfile();
      toast.success(`${sectionName} saved`);
      onDone();
    },
    onError: (error: unknown) => {
      const fieldErrors = serverFieldErrors(error);
      const reasons = Object.values(fieldErrors);
      if (reasons.length > 0) {
        showFieldErrors(fieldErrors);
        toast.error(reasons.join(". "));
        return;
      }
      toast.error(
        extractApiErrorMessage(
          error,
          `Could not save ${sectionName.toLowerCase()}`,
        ),
      );
    },
  };
}
